import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Pressable, StyleSheet, TextInput, View } from 'react-native';

import { Modal } from './modal';
import { Text } from './text';

/**
 * Compact color control: renders as a colored bar showing the current value;
 * tapping it opens a small modal with preset swatches + a free-form hex
 * override (the same pattern as the org return-address color, now reusable).
 */
export const TAG_COLOR_PRESETS = [
	'#37474f', // slate (default)
	'#6b4f00', // amber
	'#7f1d1d', // red
	'#0d47a1', // blue
	'#1b5e20', // green
	'#4a148c', // purple
	'#5d4037', // brown
	'#e65100', // orange
	'#00695c', // teal
	'#212121', // near-black
] as const;

const DEFAULT_COLOR = '#37474f';

/** Pick black/white text for legibility over an arbitrary hex background. */
const readableText = (hex: string): string => {
	const m = (hex || '').trim().match(/^#?([0-9a-f]{6})$/i);
	if (!m) return '#ffffff';
	const n = parseInt(m[1], 16);
	const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
	// Perceived luminance (ITU-R BT.601).
	return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#212121' : '#ffffff';
};

interface Props {
	value?: string;
	onChange: (color: string) => void;
	presets?: readonly string[];
	/** Hide the hex input in the modal (swatches only). */
	hideHex?: boolean;
}

export const ColorSwatchPicker: React.FC<Props> = ({ value, onChange, presets = TAG_COLOR_PRESETS, hideHex }) => {
	const [open, setOpen] = useState(false);
	const current = (value ?? '').trim();
	const shown = current || DEFAULT_COLOR;

	return (
		<>
			<Pressable
				onPress={() => setOpen(true)}
				accessibilityRole="button"
				accessibilityLabel={`Color ${current || 'not set'} — tap to change`}
				style={[styles.bar, { backgroundColor: shown }]}
			>
				<Text style={[styles.barText, { color: readableText(shown) }]} numberOfLines={1}>
					{current || 'Set color'}
				</Text>
				<Ionicons name="chevron-down" size={14} color={readableText(shown)} />
			</Pressable>

			<Modal visible={open} onDismiss={() => setOpen(false)} contentContainerStyle={styles.modal}>
				<View style={styles.header}>
					<Text style={styles.title}>Pick a color</Text>
					<Pressable onPress={() => setOpen(false)} hitSlop={8} accessibilityLabel="Close">
						<Ionicons name="close" size={22} color="#455a64" />
					</Pressable>
				</View>
				<View style={styles.swatchRow}>
					{presets.map((c) => (
						<Pressable
							key={c}
							onPress={() => onChange(c)}
							accessibilityRole="button"
							accessibilityLabel={`Color ${c}`}
							style={[styles.swatch, { backgroundColor: c }, current.toLowerCase() === c.toLowerCase() && styles.swatchOn]}
						>
							{current.toLowerCase() === c.toLowerCase() && (
								<Ionicons name="checkmark" size={16} color={readableText(c)} />
							)}
						</Pressable>
					))}
				</View>
				{!hideHex && (
					<>
						<Text style={styles.hexLabel}>Custom hex</Text>
						<View style={styles.hexRow}>
							<View style={[styles.hexPreview, { backgroundColor: shown }]} />
							<TextInput
								style={styles.hex}
								value={value ?? ''}
								onChangeText={onChange}
								placeholder="#37474f"
								placeholderTextColor="#bdbdbd"
								autoCapitalize="none"
								autoCorrect={false}
							/>
						</View>
					</>
				)}
				<Pressable style={styles.done} onPress={() => setOpen(false)}>
					<Text style={styles.doneLabel}>Done</Text>
				</Pressable>
			</Modal>
		</>
	);
};

const styles = StyleSheet.create({
	bar: {
		flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
		gap: 6, paddingVertical: 7, paddingHorizontal: 10, borderRadius: 6, minWidth: 96,
	},
	barText: { fontSize: 12, fontWeight: '600', fontFamily: 'monospace' },
	modal: { width: 320, maxWidth: '92%', borderRadius: 12, padding: 18 },
	header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
	title: { fontSize: 16, fontWeight: '700', color: '#212121' },
	swatchRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
	swatch: { width: 36, height: 36, borderRadius: 8, borderWidth: 2, borderColor: 'transparent', alignItems: 'center', justifyContent: 'center' },
	swatchOn: { borderColor: '#212121' },
	hexLabel: { fontSize: 11, color: '#546e7a', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 16, marginBottom: 6 },
	hexRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
	hexPreview: { width: 28, height: 28, borderRadius: 6, borderWidth: 1, borderColor: '#cfd8dc' },
	hex: {
		flex: 1, borderWidth: 1, borderColor: '#cfd8dc', borderRadius: 6,
		paddingVertical: 8, paddingHorizontal: 10, fontSize: 14, backgroundColor: '#fff', fontFamily: 'monospace',
	},
	done: { marginTop: 18, backgroundColor: '#1976d2', paddingVertical: 11, borderRadius: 6, alignItems: 'center' },
	doneLabel: { color: '#fff', fontWeight: '700', fontSize: 14 },
});
